import { getOptions } from 'src/providers/dataProvider'

import { LogEntry, LogFilters, LogResponse } from '../types'
import {
  buildEsQuery,
  ES_MAX_RESULT_WINDOW,
  ESSearchResponse,
  extractService,
  totalFromResponse,
  transformHit,
} from './elasticQuery'

interface EnvConfig {
  LOG_BACKEND_ENABLED?: string
  API_URL?: string
}

function readEnv(): EnvConfig {
  if (typeof window === 'undefined') return {}
  const w = window as unknown as { _env_?: EnvConfig }
  return w._env_ ?? {}
}

function isBackendConfigured(): boolean {
  return (readEnv().LOG_BACKEND_ENABLED ?? '').length > 0
}

function logsSearchUrl(): string {
  const base = (readEnv().API_URL ?? '').replace(/\/$/, '')
  return `${base}/log/api/Logs/search`
}

function backendHeaders(): Headers {
  const headers = getOptions().headers
  headers.set('Content-Type', 'application/json')
  return headers
}

async function backendError(res: Response, context: string): Promise<Error> {
  if (res.status === 401) {
    return new Error(
      `${context}: your session has expired or you are not signed in. Please sign in again.`,
    )
  }
  if (res.status === 403) {
    return new Error(
      `${context}: you don't have permission to view logs (supervisor role required).`,
    )
  }
  // Other failures: the proxy forwards Elasticsearch's structured error body
  // verbatim, so surface its reason instead of a bare status.
  let detail = ''
  try {
    const body = (await res.json()) as {
      error?: { type?: string; reason?: string }
    }
    const reason = body.error?.reason
    const type = body.error?.type
    if (typeof reason === 'string' && reason.length > 0) {
      detail = type != null ? ` — ${type}: ${reason}` : ` — ${reason}`
    }
  } catch {
    // not JSON — fall back to status text only
  }
  return new Error(`${context}: ${res.status} ${res.statusText}${detail}`)
}

async function postLogsSearch(
  body: unknown,
  context: string,
): Promise<unknown> {
  const res = await fetch(logsSearchUrl(), {
    method: 'POST',
    headers: backendHeaders(),
    body: JSON.stringify(body),
  })
  if (!res.ok) throw await backendError(res, context)
  return await res.json()
}

export async function fetchLogs(filters: LogFilters): Promise<LogResponse> {
  const response = isBackendConfigured()
    ? await fetchLogsFromBackend(filters)
    : await fetchLogsFromMockApi(filters)
  return response
}

export async function fetchSubsystems(): Promise<string[]> {
  if (isBackendConfigured()) return await fetchSubsystemsFromBackend()
  return await fetchSubsystemsFromMockApi()
}

export async function fetchUsers(): Promise<string[]> {
  if (isBackendConfigured()) return await fetchUsersFromBackend()
  return await fetchUsersFromMockApi()
}

export async function fetchStates(): Promise<string[]> {
  if (isBackendConfigured()) return await fetchStatesFromBackend()
  return await fetchStatesFromMockApi()
}

export async function fetchEntityTypes(): Promise<string[]> {
  if (isBackendConfigured()) return await fetchEntityTypesFromBackend()
  return await fetchEntityTypesFromMockApi()
}

async function fetchLogsFromBackend(filters: LogFilters): Promise<LogResponse> {
  const body = buildEsQuery(filters)
  const json = (await postLogsSearch(
    body,
    'Failed to fetch logs',
  )) as ESSearchResponse
  const data: LogEntry[] = json.hits.hits.map(transformHit)

  const total = totalFromResponse(json)
  // Elasticsearch can only serve the first ES_MAX_RESULT_WINDOW hits, so cap
  // the page count to what is reachable — otherwise the UI offers pages whose
  // deep-pagination request fails and strands the user.
  const reachable = Math.min(total, ES_MAX_RESULT_WINDOW)
  const totalPages = total === 0 ? 0 : Math.ceil(reachable / filters.limit)

  return {
    data,
    pagination: {
      page: filters.page,
      limit: filters.limit,
      total,
      totalPages,
    },
  }
}

interface ESSubsystemsAggResponse {
  aggregations?: {
    subsystems?: { buckets: Array<{ key: string }> }
  }
}

async function fetchSubsystemsFromBackend(): Promise<string[]> {
  const body = {
    size: 0,
    aggs: {
      subsystems: {
        terms: { field: '_index', size: 1000, order: { _key: 'asc' } },
      },
    },
  }
  const json = (await postLogsSearch(
    body,
    'Failed to fetch subsystems',
  )) as ESSubsystemsAggResponse
  const buckets = json.aggregations?.subsystems?.buckets ?? []
  return [
    ...new Set(buckets.map(b => extractService(b.key).toUpperCase())),
  ].sort()
}

async function fetchLogsFromMockApi(filters: LogFilters): Promise<LogResponse> {
  const params = new URLSearchParams()
  params.set('search', filters.search)
  params.set('level', filters.level)
  for (const subsystem of filters.subsystems) {
    params.append('subsystem', subsystem)
  }
  params.set('datePreset', filters.datePreset)
  if (filters.dateFrom !== null && filters.dateFrom.length > 0) {
    params.set('dateFrom', filters.dateFrom)
  }
  if (filters.dateTo !== null && filters.dateTo.length > 0) {
    params.set('dateTo', filters.dateTo)
  }
  for (const eventType of filters.eventTypes) {
    params.append('eventType', eventType)
  }
  for (const user of filters.users) params.append('user', user)
  for (const state of filters.states) params.append('state', state)
  for (const entityType of filters.entityTypes) {
    params.append('entityType', entityType)
  }
  for (const outcome of filters.outcomes) params.append('outcome', outcome)
  params.set('traceId', filters.traceId)
  params.set('requestId', filters.requestId)
  params.set('sortField', filters.sortField)
  params.set('sortOrder', filters.sortOrder)
  params.set('page', String(filters.page))
  params.set('limit', String(filters.limit))

  const response = await fetch(`/api/logs?${params.toString()}`)
  if (!response.ok) {
    throw new Error(
      `Failed to fetch logs: ${response.status} ${response.statusText}`,
    )
  }
  return (await response.json()) as LogResponse
}

async function fetchSubsystemsFromMockApi(): Promise<string[]> {
  const response = await fetch('/api/logs/subsystems')
  if (!response.ok) {
    throw new Error(
      `Failed to fetch subsystems: ${response.status} ${response.statusText}`,
    )
  }
  return (await response.json()) as string[]
}

interface ESUsersAggResponse {
  aggregations?: {
    users_legacy?: { buckets: Array<{ key: string }> }
    users_new?: { buckets: Array<{ key: string }> }
  }
}

async function fetchUsersFromBackend(): Promise<string[]> {
  // A single `terms` agg can't span two field paths, so legacy and
  // new-schema are fetched as separate aggs and merged below.
  const body = {
    size: 0,
    aggs: {
      users_legacy: {
        terms: {
          field: 'fields.Username.keyword',
          size: 1000,
          order: { _key: 'asc' },
        },
      },
      users_new: {
        terms: { field: 'user_name', size: 1000, order: { _key: 'asc' } },
      },
    },
  }
  const json = (await postLogsSearch(
    body,
    'Failed to fetch users',
  )) as ESUsersAggResponse
  const legacy = json.aggregations?.users_legacy?.buckets ?? []
  const fresh = json.aggregations?.users_new?.buckets ?? []
  return [
    ...new Set(
      [...legacy, ...fresh]
        .map(b => b.key)
        .filter(key => typeof key === 'string' && key.length > 0),
    ),
  ].sort()
}

async function fetchUsersFromMockApi(): Promise<string[]> {
  const response = await fetch('/api/logs/users')
  if (!response.ok) {
    throw new Error(
      `Failed to fetch users: ${response.status} ${response.statusText}`,
    )
  }
  return (await response.json()) as string[]
}

interface ESStatesAggResponse {
  aggregations?: {
    states_legacy?: { buckets: Array<{ key: string }> }
    states_new?: { buckets: Array<{ key: string }> }
  }
}

async function fetchStatesFromBackend(): Promise<string[]> {
  const body = {
    size: 0,
    aggs: {
      states_legacy: {
        terms: {
          field: 'fields.CommandType.keyword',
          size: 1000,
          order: { _key: 'asc' },
        },
      },
      states_new: {
        terms: { field: 'event_type', size: 1000, order: { _key: 'asc' } },
      },
    },
  }
  const json = (await postLogsSearch(
    body,
    'Failed to fetch states',
  )) as ESStatesAggResponse
  const legacy = json.aggregations?.states_legacy?.buckets ?? []
  const fresh = json.aggregations?.states_new?.buckets ?? []
  return [
    ...new Set(
      [...legacy, ...fresh]
        .map(b => b.key)
        .filter(key => typeof key === 'string' && key.length > 0),
    ),
  ].sort()
}

async function fetchStatesFromMockApi(): Promise<string[]> {
  const response = await fetch('/api/logs/states')
  if (!response.ok) {
    throw new Error(
      `Failed to fetch states: ${response.status} ${response.statusText}`,
    )
  }
  return (await response.json()) as string[]
}

interface ESEntityTypesAggResponse {
  aggregations?: {
    entityTypes?: { buckets: Array<{ key: string }> }
  }
}

async function fetchEntityTypesFromBackend(): Promise<string[]> {
  const body = {
    size: 0,
    aggs: {
      entityTypes: {
        terms: {
          field: 'constellation_entity_type',
          size: 1000,
          order: { _key: 'asc' },
        },
      },
    },
  }
  const json = (await postLogsSearch(
    body,
    'Failed to fetch entity types',
  )) as ESEntityTypesAggResponse
  const buckets = json.aggregations?.entityTypes?.buckets ?? []
  return buckets
    .map(b => b.key)
    .filter(key => typeof key === 'string' && key.length > 0)
}

async function fetchEntityTypesFromMockApi(): Promise<string[]> {
  const response = await fetch('/api/logs/entityTypes')
  if (!response.ok) {
    throw new Error(
      `Failed to fetch entity types: ${response.status} ${response.statusText}`,
    )
  }
  return (await response.json()) as string[]
}
